import type { BimContractOtherCostData, ContractOtherCostUpdateData, ChapterEnum } from 'gql/graphql';
import { SectionEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import type { IContractOtherCostLimitSpanSelectionItem } from '../../../../UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import type { TypeItem } from '../../OtherCreate/commonData';
import { SeasonItemsIdsEnum, OTHER_SECTION_OR_CHAPTER, seasonByDistributionMethod } from '../../OtherCreate/commonData';

export const getUerLimitsValues = (
    data: ContractOtherCostUpdateData,
    limitSpanData: IContractOtherCostLimitSpanSelectionItem[],
) =>
    data?.groupConstructionLimits.reduce(
        (
            limits: {
                cost: { [key: number]: number };
                method: { [key: number]: Record<string, string> };
                manualDistribution: { [key: number]: Record<`season_${number}`, string> };
                limitSpanInputs: { [key: number]: IContractOtherCostLimitSpanSelectionItem[] };
                hasActualCost: { [key: number]: boolean };
                hasLimitSpan: { [key: number]: boolean };
            },
            item,
        ) => {
            const currentId = item.constructionType.id;

            limits.limitSpanInputs[currentId] = limitSpanData.filter(
                (limitItem) => limitItem.constructionTypeId === currentId && limitItem.isSelected,
            );

            limits.cost[currentId] = item.cost;
            limits.method[currentId] = seasonByDistributionMethod[item.distributionMethod];
            limits.hasActualCost[currentId] = item.hasActualCost;
            limits.hasLimitSpan[currentId] = item.hasLimitSpan;

            if (seasonByDistributionMethod[item.distributionMethod]?.id === SeasonItemsIdsEnum.MANUAL) {
                data?.limits?.forEach((limit) => {
                    if (limit.constructionType?.id === currentId) {
                        limits.manualDistribution[currentId] = {
                            ...limits.manualDistribution[currentId],
                            [`season_${limit.season?.id}`]: limit.cost,
                        };
                    }
                });
            }

            return limits;
        },
        { cost: {}, method: {}, manualDistribution: [], limitSpanInputs: {}, hasActualCost: {}, hasLimitSpan: {} },
    );

export const getBimLimitsValues = (data: BimContractOtherCostData) =>
    data.otherCostLimits.reduce(
        (
            limits: {
                cost: { [key: number]: number };
                method: { [key: number]: Record<string, string> };
            },
            item,
        ) => {
            const currentId = item.constructionType.id;

            limits.cost[currentId] = item.cost;

            return limits;
        },
        { cost: {}, method: {} },
    );

interface IGetUpdateInput {
    contractId: number;
    otherId: number;
    selectedType: TypeItem;
    selectedChapterOrSection: { id: ChapterEnum; label: string } | { id: SectionEnum; label: string };
    isCalculationTypeEditable: boolean;
    limitsArray: any[];
    data: any;
}

export const getUpdateInput = (props: IGetUpdateInput) => {
    const {
        contractId,
        selectedChapterOrSection,
        selectedType,
        isCalculationTypeEditable,
        otherId,
        limitsArray,
        data,
    } = props;

    const chapter: ChapterEnum | null =
        selectedChapterOrSection === OTHER_SECTION_OR_CHAPTER[0] ? (selectedChapterOrSection.id as ChapterEnum) : null;
    const section: SectionEnum =
        selectedChapterOrSection === OTHER_SECTION_OR_CHAPTER[1]
            ? (selectedChapterOrSection.id as SectionEnum)
            : SectionEnum.Others;

    const calculatingType =
        selectedType && isCalculationTypeEditable
            ? {
                  calculatingType: selectedType.id,
              }
            : {};

    const calculatingTypeValue = data?.calculatingTypeValue
        ? {
              calculatingTypeValue: replacerCost(data.calculatingTypeValue),
          }
        : {};

    const catalogOtherCostTypeId = data?.otherCostType ? { catalogOtherCostTypeId: data.otherCostType?.id } : {};

    return {
        updateInput: {
            ...calculatingType,
            ...calculatingTypeValue,
            contractId,
            id: otherId,
            limits: limitsArray,
            name: data.name,
            ...catalogOtherCostTypeId,
            key: data.key,
        },
        chapter,
        section,
    };
};
